import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  IconButton,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { renderPipeTrunkV2 } from "../../../print-v2";
import { useVoyageContextV2 } from "../../../Contexts/VoyageContextV2";
import { getVoyageNumber } from "../../../utils/domainSelectors";
import { getSelectedCargo, getSelectedTrip } from "../../../utils/voyageSelection";

const PipeTrunkEntry = ({ permit, voyage, companyLogo, isEditMode }) => {
  const { selectedTripId, selectedCargoIndex } = useVoyageContextV2();
  const [formState, setFormState] = useState({
    // SECTION 1
    ventilated: false,
    oxygen: "",
    hydrocarbon: "",
    h2s: "",
    benzene: "",
    co: "",
    toxicGas: "",
    rescueEquipment: false,
    illumination: false,
    oowInformed: false,
    approvedEquipment: false,

    // SECTION 2
    emergencyProcedure: false,
    communicationChannel: "",
    reportingInterval: "",
    personalGasAlarm: false,
    airIntakes: false,

    // Endorsements
    endorsements: [],
  });

  const handleInputChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, checked) => {
    setFormState((prev) => ({ ...prev, [field]: checked }));
  };

  const handleEndorsementChange = (index, field, value) => {
    const updatedEndorsements = [...formState.endorsements];
    updatedEndorsements[index][field] = value;
    setFormState((prev) => ({ ...prev, endorsements: updatedEndorsements }));
  };

  const addEndorsement = () => {
    setFormState((prev) => ({
      ...prev,
      endorsements: [
        ...prev.endorsements,
        { position: "", name: "", signature: "", date: "", time: "" },
      ],
    }));
  };

  const removeEndorsement = (index) => {
    const updated = [...formState.endorsements];
    updated.splice(index, 1);
    setFormState((prev) => ({ ...prev, endorsements: updated }));
  };

  const handlePrint = () => {
    const selectedTrip = getSelectedTrip(voyage, selectedTripId);
    const selectedCargo = getSelectedCargo(
      voyage,
      selectedTripId,
      selectedCargoIndex
    );

    renderPipeTrunkV2({
      companyLogo,
      formData: {
      shipName: voyage.shipName,
      voyageNumber: getVoyageNumber(voyage),
      port: selectedTrip?.port || selectedTrip?.from || selectedTrip?.to || "",
      cargo: selectedCargo?.cargo || "",

      permit: { ...permit, ...formState },
      },
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6">{permit.title}</Typography>

      {/* SECTION 1 - Pre-Entry Preparations */}
      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
        SECTION 1 - Pre-Entry Preparations
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={formState.ventilated}
            disabled={!isEditMode}
            onChange={(e) =>
              handleCheckboxChange("ventilated", e.target.checked)
            }
          />
        }
        label="Has the space been thoroughly ventilated via the fixed ventilation system?"
      />
      <TextField
        label="Oxygen [% VOL]"
        value={formState.oxygen}
        disabled={!isEditMode}
        onChange={(e) => handleInputChange("oxygen", e.target.value)}
      />
      <TextField
        label="Hydrocarbon [% LFL]"
        value={formState.hydrocarbon}
        disabled={!isEditMode}
        onChange={(e) => handleInputChange("hydrocarbon", e.target.value)}
      />
      <TextField
        label="H2S [ppm]"
        value={formState.h2s}
        disabled={!isEditMode}
        onChange={(e) => handleInputChange("h2s", e.target.value)}
      />
      <TextField
        label="Benzene [ppm]"
        value={formState.benzene}
        disabled={!isEditMode}
        onChange={(e) => handleInputChange("benzene", e.target.value)}
      />
      <TextField
        label="CO [ppm]"
        value={formState.co}
        disabled={!isEditMode}
        onChange={(e) => handleInputChange("co", e.target.value)}
      />
      <TextField
        label="Toxic gases [Specify & PEL / ppm]"
        value={formState.toxicGas}
        disabled={!isEditMode}
        onChange={(e) => handleInputChange("toxicGas", e.target.value)}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={formState.rescueEquipment}
            disabled={!isEditMode}
            onChange={(e) =>
              handleCheckboxChange("rescueEquipment", e.target.checked)
            }
          />
        }
        label="Is rescue and resuscitation equipment available for immediate use?"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={formState.illumination}
            disabled={!isEditMode}
            onChange={(e) =>
              handleCheckboxChange("illumination", e.target.checked)
            }
          />
        }
        label="Adequate illumination provided?"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={formState.oowInformed}
            disabled={!isEditMode}
            onChange={(e) =>
              handleCheckboxChange("oowInformed", e.target.checked)
            }
          />
        }
        label="The responsible OOW informed before entry?"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={formState.approvedEquipment}
            disabled={!isEditMode}
            onChange={(e) =>
              handleCheckboxChange("approvedEquipment", e.target.checked)
            }
          />
        }
        label="Is all equipment used of an approved type?"
      />

      {/* SECTION 2 - Pre-Entry Checks */}
      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
        SECTION 2 - Pre-Entry Checks
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={formState.emergencyProcedure}
            disabled={!isEditMode}
            onChange={(e) =>
              handleCheckboxChange("emergencyProcedure", e.target.checked)
            }
          />
        }
        label="Emergency and evacuation procedures have been agreed"
      />
      <TextField
        label="Radio Channel"
        value={formState.communicationChannel}
        disabled={!isEditMode}
        onChange={(e) =>
          handleInputChange("communicationChannel", e.target.value)
        }
      />
      <TextField
        label="Reporting interval [minutes]"
        value={formState.reportingInterval}
        disabled={!isEditMode}
        onChange={(e) => handleInputChange("reportingInterval", e.target.value)}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={formState.personalGasAlarm}
            disabled={!isEditMode}
            onChange={(e) =>
              handleCheckboxChange("personalGasAlarm", e.target.checked)
            }
          />
        }
        label="Each person working in this area shall carry/use a personal multiple gas detecting alarm unit"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={formState.airIntakes}
            disabled={!isEditMode}
            onChange={(e) =>
              handleCheckboxChange("airIntakes", e.target.checked)
            }
          />
        }
        label="Check that air intakes are fully open and the exhaust fan is running"
      />

      {/* ENDORSEMENTS */}
      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
        Endorsements
      </Typography>
      {formState.endorsements.map((e, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            mb: 1,
          }}
        >
          <TextField
            label="Position"
            value={e.position}
            disabled={!isEditMode}
            onChange={(ev) =>
              handleEndorsementChange(i, "position", ev.target.value)
            }
          />
          <TextField
            label="Name"
            value={e.name}
            disabled={!isEditMode}
            onChange={(ev) =>
              handleEndorsementChange(i, "name", ev.target.value)
            }
          />

          {isEditMode && (
            <IconButton onClick={() => removeEndorsement(i)}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ))}
      {isEditMode && (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addEndorsement}
          sx={{ mt: 1 }}
        >
          Add Person
        </Button>
      )}

      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrint}
          startIcon={<PrintIcon />}
        >
          Print Permit
        </Button>
      </Box>
    </Box>
  );
};

export default PipeTrunkEntry;
